<template>
  <div class="pd-filter-component" :class="{ 'is-expanded': isExpanded }">
    <!-- Header -->
    <button
      type="button"
      class="filter-header"
      :aria-expanded="isExpanded"
      aria-controls="pd-filter-navigation"
      @click="toggleMode"
    >
      <div class="header-content">
        <span class="header-title">All Filters</span>
        <div class="filter-icon" aria-hidden="true">
          <svg
            width="20"
            height="16"
            viewBox="0 0 20 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.75 2.75C0.75 3.85457 1.64543 4.75 2.75 4.75C3.85457 4.75 4.75 3.85457 4.75 2.75C4.75 1.64543 3.85457 0.75 2.75 0.75C1.64543 0.75 0.75 1.64543 0.75 2.75Z"
              fill="#ffffff"
            />
            <path
              d="M18.75 12.75C18.75 13.8546 17.8546 14.75 16.75 14.75C15.6454 14.75 14.75 13.8546 14.75 12.75C14.75 11.6454 15.6454 10.75 16.75 10.75C17.8546 10.75 18.75 11.6454 18.75 12.75Z"
              fill="#ffffff"
            />
            <path
              d="M18.75 3.5C19.1642 3.5 19.5 3.16421 19.5 2.75C19.5 2.33579 19.1642 2 18.75 2V2.75V3.5ZM0.75 12C0.335786 12 -5.96046e-08 12.3358 0 12.75C5.96046e-08 13.1642 0.335787 13.5 0.75 13.5L0.75 12.75L0.75 12ZM8.75 2.75V3.5L18.75 3.5V2.75V2L8.75 2V2.75ZM0.75 2.75H0C0 4.26878 1.23122 5.5 2.75 5.5V4.75V4C2.05964 4 1.5 3.44036 1.5 2.75H0.75ZM2.75 4.75V5.5C4.26878 5.5 5.5 4.26878 5.5 2.75H4.75H4C4 3.44036 3.44036 4 2.75 4V4.75ZM4.75 2.75H5.5C5.5 1.23122 4.26878 0 2.75 0V0.75V1.5C3.44036 1.5 4 2.05964 4 2.75H4.75ZM2.75 0.75V0C1.23122 0 0 1.23122 0 2.75H0.75H1.5C1.5 2.05964 2.05964 1.5 2.75 1.5V0.75ZM10.75 12.75V12L0.75 12L0.75 12.75L0.75 13.5L10.75 13.5V12.75ZM18.75 12.75H18C18 13.4404 17.4404 14 16.75 14V14.75V15.5C18.2688 15.5 19.5 14.2688 19.5 12.75H18.75ZM16.75 14.75V14C16.0596 14 15.5 13.4404 15.5 12.75H14.75H14C14 14.2688 15.2312 15.5 16.75 15.5V14.75ZM14.75 12.75H15.5C15.5 12.0596 16.0596 11.5 16.75 11.5V10.75V10C15.2312 10 14 11.2312 14 12.75H14.75ZM16.75 10.75V11.5C17.4404 11.5 18 12.0596 18 12.75H18.75H19.5C19.5 11.2312 18.2688 10 16.75 10V10.75Z"
              fill="#ffffff"
            />
          </svg>
        </div>
      </div>
    </button>

    <!-- Filter Navigation -->
    <div
      id="pd-filter-navigation"
      v-show="isExpanded"
      role="navigation"
      aria-label="Filter options"
      class="filter-navigation"
    >
      <!-- Grade -->
      <FilterAccordionSection
        v-model="localSelectedGrades"
        title="Grade"
        section-id="pd-section-grades"
        :options="grades"
        :expanded="expandedSections.grades"
        @toggle="toggleSection('grades')"
        @change="handleFilterChange"
      />

      <div class="section-divider"></div>

      <!-- Subject -->
      <FilterAccordionSection
        v-model="localSelectedSubjects"
        title="Subject"
        section-id="pd-section-subjects"
        :options="subjects"
        :expanded="expandedSections.subjects"
        @toggle="toggleSection('subjects')"
        @change="handleFilterChange"
      />

      <div class="section-divider"></div>

      <!-- Tech Domain -->
      <FilterAccordionSection
        v-model="localSelectedDomains"
        title="Tech Domain"
        section-id="pd-section-domains"
        :options="domains"
        :expanded="expandedSections.domains"
        @toggle="toggleSection('domains')"
        @change="handleFilterChange"
      />
    </div>

    <!-- Filter Navigation - Compact (Dropdowns) -->
    <div
      v-show="!isExpanded"
      role="navigation"
      aria-label="Filter dropdown options"
      class="filter-navigation"
    >
      <div class="desktop-filters">
        <FilterDropdown
          v-model="localSelectedGrades"
          :options="grades"
          label="Grade"
          variant="purple"
          @change="handleFilterChange"
        />

        <FilterDropdown
          v-model="localSelectedSubjects"
          :options="subjects"
          label="Subject"
          variant="purple"
          @change="handleFilterChange"
        />

        <FilterDropdown
          v-model="localSelectedDomains"
          :options="domains"
          label="Tech Domain"
          variant="purple"
          @change="handleFilterChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, watch, onMounted } from "vue";
import CategoryAdminService from "@/services/categoryAdminService.js";
import FilterDropdown from "@/components/maker-cart/FilterDropdown.vue";
import FilterAccordionSection from "@/components/shared/FilterAccordionSection.vue";

const props = defineProps({
  selectedSubjects: {
    type: Array,
    default: () => [],
  },
  selectedGrades: {
    type: Array,
    default: () => [],
  },
  selectedDomains: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["filter-change", "reset-filters", "mode-change"]);

const isExpanded = ref(false);

const toggleMode = () => {
  isExpanded.value = !isExpanded.value;
  emit("mode-change", isExpanded.value);
};

const expandedSections = ref({
  subjects: true,
  grades: true,
  domains: true,
});

const subjects = ref([]);
const grades = ref([]);
const domains = ref([]);

// Local state for filters
const localSelectedSubjects = ref([...props.selectedSubjects]);
const localSelectedGrades = ref([...props.selectedGrades]);
const localSelectedDomains = ref([...props.selectedDomains]);

const selectedFiltersCount = computed(() => {
  return (
    localSelectedSubjects.value.length +
    localSelectedGrades.value.length +
    localSelectedDomains.value.length
  );
});

// Watch for prop changes to sync local state
watch(
  () => props.selectedSubjects,
  (newValue) => {
    localSelectedSubjects.value = [...newValue];
  },
  { deep: true }
);

watch(
  () => props.selectedGrades,
  (newValue) => {
    localSelectedGrades.value = [...newValue];
  },
  { deep: true }
);

watch(
  () => props.selectedDomains,
  (newValue) => {
    localSelectedDomains.value = [...newValue];
  },
  { deep: true }
);

const fetchFilterOptions = async () => {
  try {
    const [domainRes, subjectRes, gradeRes] = await Promise.all([
      CategoryAdminService.getById(30), // Domain
      CategoryAdminService.getById(33), // Subject
      CategoryAdminService.getById(26), // Grade
    ]);

    if (domainRes.status === 200 && domainRes.data.data?.terms) {
      const filteredTerms = domainRes.data.data.terms.filter(
        (term) => term.locale !== "SG"
      );
      domains.value = filteredTerms.map((term) => ({
        text: term.title,
        value: term.title,
      }));
    }
    if (subjectRes.status === 200 && subjectRes.data.data?.terms) {
      const filteredTerms = subjectRes.data.data.terms.filter(
        (term) => term.locale !== "SG"
      );
      subjects.value = filteredTerms.map((term) => ({
        text: term.title,
        value: term.title,
      }));
    }
    if (gradeRes.status === 200 && gradeRes.data.data?.terms) {
      const filteredTerms = gradeRes.data.data.terms.filter(
        (term) => term.locale !== "SG"
      );
      grades.value = filteredTerms.map((term) => ({
        text: term.title,
        value: term.title,
      }));
    }
  } catch (error) {
    console.error("Error fetching filter options:", error);
  }
};

const handleFilterChange = () => {
  emitFilterChange();
};

const emitFilterChange = () => {
  emit("filter-change", {
    subjects: localSelectedSubjects.value,
    grades: localSelectedGrades.value,
    domains: localSelectedDomains.value,
  });
};

const resetFilters = () => {
  localSelectedSubjects.value = [];
  localSelectedGrades.value = [];
  localSelectedDomains.value = [];
  emit("reset-filters");
};

function toggleSection(section) {
  expandedSections.value[section] = !expandedSections.value[section];
}

onMounted(async () => {
  await fetchFilterOptions();
});
</script>

<style scoped>
.pd-filter-component {
  --filter-section-color: var(--primary-purple);
  --filter-section-focus-color: var(--primary-green, #00925e);
  position: relative;
  box-shadow:
    0px 6px 8px 4px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  overflow: visible; /* Allow Dropdowns to overflow right */
  width: 100%;
  display: flex;
  flex-direction: column;
}

.pd-filter-component.is-expanded {
  overflow: hidden;
}

/* Header section styling */
.filter-header {
  background-color: var(--purple-dark-1);
  padding: 8px;
  border-radius: 12px 12px 0 0;
  flex-shrink: 0;
  cursor: pointer;
  transition: background-color 0.2s ease;
  width: 100%;
  border: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  text-align: left;
  color: var(--black-body-text);
}

.filter-header:focus-visible {
  outline: 3px solid #ffffff;
  outline-offset: -3px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  height: 48px;
  border-radius: 12px;
}

.header-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: white;
  letter-spacing: 0.024px;
}

.filter-icon {
  color: white;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Navigation section styling */
.filter-navigation {
  background-color: #ffffff;
  padding: 8px;
  border-radius: 0 0 12px 12px;
}

.desktop-filters {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-divider {
  height: 1px;
  background-color: var(--purple-dark-1);
  margin: 0 16px;
}

.filter-actions {
  padding: 8px;
  display: flex;
  justify-content: center;
  border-top: 1px solid #e0e0e0;
  margin-top: 8px;
}

.reset-filter-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: transparent;
  color: var(--primary-pink);
  border: none;
  font-size: var(--fs-small);
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 20px;
  transition: background-color 0.2s ease;
}

.reset-filter-btn:hover {
  background-color: rgba(76, 77, 220, 0.1);
}

.reset-filter-btn:focus-visible {
  outline: 2px solid var(--primary-blue);
  outline-offset: 2px;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .pd-filter-component {
    display: none;
  }
}
</style>
